import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Lock, Unlock, BookOpen, Clock, Users, Award, CheckCircle, ChevronDown, ChevronUp, Play, FileText, Calculator, ClipboardCheck, ArrowLeft } from 'lucide-react';
import { IFC_CHAPTERS, NUMERICAL_PRACTICE, MOCK_EXAMS, IFC_EXAM_INFO } from '../data/ifcCourseData';

// Import chapter components (will be lazy loaded)
const ChapterComponents = {
  1: React.lazy(() => import('../components/ifc-course/Chapter1')),
  2: React.lazy(() => import('../components/ifc-course/Chapter2')),
  3: React.lazy(() => import('../components/ifc-course/Chapter3')),
  4: React.lazy(() => import('../components/ifc-course/Chapter4')),
  5: React.lazy(() => import('../components/ifc-course/Chapter5')),
  6: React.lazy(() => import('../components/ifc-course/Chapter6')),
  7: React.lazy(() => import('../components/ifc-course/Chapter7')),
  8: React.lazy(() => import('../components/ifc-course/Chapter8')),
  9: React.lazy(() => import('../components/ifc-course/Chapter9')),
  10: React.lazy(() => import('../components/ifc-course/Chapter10')),
  11: React.lazy(() => import('../components/ifc-course/Chapter11')),
  12: React.lazy(() => import('../components/ifc-course/Chapter12')),
  13: React.lazy(() => import('../components/ifc-course/Chapter13')),
  14: React.lazy(() => import('../components/ifc-course/Chapter14')),
  15: React.lazy(() => import('../components/ifc-course/Chapter15')),
  16: React.lazy(() => import('../components/ifc-course/Chapter16')),
  17: React.lazy(() => import('../components/ifc-course/Chapter17')),
  18: React.lazy(() => import('../components/ifc-course/Chapter18')),
  numerical: React.lazy(() => import('../components/ifc-course/NumericalsMastery')),
  mock1: React.lazy(() => import('../components/ifc-course/MockExam1')),
  mock2: React.lazy(() => import('../components/ifc-course/MockExam2')),
  mock3: React.lazy(() => import('../components/ifc-course/MockExam3')),
  mock4: React.lazy(() => import('../components/ifc-course/MockExamCaseStudies')),
  mock5: React.lazy(() => import('../components/ifc-course/MockExamNumericals')),
  mock6: React.lazy(() => import('../components/ifc-course/MockExamFinal')),
};

const IFC = () => {
  const [accessCode, setAccessCode] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [accessError, setAccessError] = useState('');
  const [activeChapter, setActiveChapter] = useState(null);
  const [showSyllabus, setShowSyllabus] = useState(false);

  // Check for saved access on mount
  useEffect(() => {
    const savedAccess = localStorage.getItem('ifc_course_access');
    if (savedAccess === 'unlocked') {
      setIsUnlocked(true);
    }
  }, []);

  // Valid access codes
  const VALID_CODES = ['EDUFINWIZARDS2026APRILHELLO'];

  const handleAccessSubmit = (e) => {
    e.preventDefault();
    if (VALID_CODES.includes(accessCode.toUpperCase().trim())) {
      setIsUnlocked(true);
      localStorage.setItem('ifc_course_access', 'unlocked');
      setShowAccessModal(false);
      setAccessError('');
    } else {
      setAccessError('Invalid access code. Please check your code and try again.');
    }
  };

  const handleLogout = () => {
    setIsUnlocked(false);
    localStorage.removeItem('ifc_course_access');
    setActiveChapter(null);
  };

  // If viewing a chapter, show chapter content
  if (activeChapter !== null && isUnlocked) {
    const ChapterComponent = ChapterComponents[activeChapter];
    
    if (ChapterComponent) {
      return (
        <React.Suspense fallback={
          <div className="min-h-screen bg-slate-900 flex items-center justify-center">
            <div className="text-white text-xl">Loading chapter...</div>
          </div>
        }>
          <div className="min-h-screen bg-slate-50">
            <div className="bg-slate-900 py-4 px-6">
              <button
                onClick={() => setActiveChapter(null)}
                className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Course
              </button>
            </div>
            <ChapterComponent />
          </div>
        </React.Suspense>
      );
    } else {
      // Chapter not yet available
      const chapter = IFC_CHAPTERS.find(c => c.id === activeChapter);
      return (
        <div className="min-h-screen bg-slate-50">
          <div className="bg-slate-900 py-4 px-6">
            <button
              onClick={() => setActiveChapter(null)}
              className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Course
            </button>
          </div>
          <div className="max-w-4xl mx-auto py-16 px-6">
            <h1 className="text-3xl font-bold text-slate-900 mb-6">
              Chapter {activeChapter}: {chapter?.title}
            </h1>
            
            {/* Video Section */}
            {chapter?.video && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-4">Video Lecture</h2>
                <div className="aspect-video bg-slate-900 rounded-xl overflow-hidden">
                  <iframe
                    src={chapter.video.replace('youtu.be/', 'youtube.com/embed/').replace('watch?v=', 'embed/')}
                    title={`Chapter ${activeChapter} Video`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            {/* Multiple Videos */}
            {chapter?.videos && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-4">Video Lectures</h2>
                <div className="grid gap-4">
                  {chapter.videos.map((vid, idx) => (
                    <div key={idx}>
                      <h3 className="text-lg font-medium text-slate-700 mb-2">{vid.title}</h3>
                      <div className="aspect-video bg-slate-900 rounded-xl overflow-hidden">
                        <iframe
                          src={vid.url.replace('youtu.be/', 'youtube.com/embed/').replace('watch?v=', 'embed/')}
                          title={`${vid.title}`}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Podcast Section */}
            {chapter?.podcast && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-4">Audio Podcast</h2>
                <div className="aspect-video bg-slate-900 rounded-xl overflow-hidden">
                  <iframe
                    src={chapter.podcast.replace('youtu.be/', 'youtube.com/embed/').replace('watch?v=', 'embed/')}
                    title={`Chapter ${activeChapter} Podcast`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <p className="text-amber-800">
                Interactive study materials for this chapter are coming soon. 
                In the meantime, please use the video lecture and podcast above.
              </p>
            </div>
          </div>
        </div>
      );
    }
  }

  return (
    <>
      <Helmet>
        <title>IFC Exam Prep Course | Investment Funds in Canada | Harry Vadalkar</title>
        <meta name="description" content="Complete IFC (Investment Funds in Canada) exam preparation course. 18 chapters, practice exams, study notes, and video tutorials. Pass your IFIC exam with confidence." />
        <meta name="keywords" content="IFC exam, Investment Funds in Canada, IFIC exam prep, mutual fund course, IFC certification, Canadian investment course, MFDA exam" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://harryvadalkar.ca/ifc" />
        <meta property="og:title" content="IFC Exam Prep Course | Investment Funds in Canada" />
        <meta property="og:description" content="Complete IFC exam preparation with 18 chapters, 6 mock exams, and comprehensive study materials." />
        <meta property="og:image" content="https://img.youtube.com/vi/WGyGpgzQpGM/maxresdefault.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="IFC Exam Prep Course | Investment Funds in Canada" />
        <meta name="twitter:description" content="Complete IFC exam preparation with 18 chapters, 6 mock exams, and comprehensive study materials." />
        <meta name="twitter:image" content="https://img.youtube.com/vi/WGyGpgzQpGM/maxresdefault.jpg" />
        <link rel="canonical" href="https://harryvadalkar.ca/ifc" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
        {/* Hero Section */}
        <div className="relative py-20 px-6">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/20 to-blue-900/20" />
          <div className="relative max-w-6xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Investment Funds in Canada
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
              Complete IFC Exam Preparation Course
            </p>
            
            {/* Promo Video */}
            <div className="max-w-3xl mx-auto mb-12">
              <div className="relative rounded-2xl overflow-hidden border border-slate-700 shadow-2xl">
                <div className="relative pb-[56.25%] h-0">
                  <iframe 
                    src="https://www.youtube.com/embed/WGyGpgzQpGM" 
                    title="IFC Course Introduction"
                    className="absolute top-0 left-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                    allowFullScreen
                  />
                </div>
              </div>
            </div>

            {/* Feature Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 text-left max-w-5xl mx-auto mb-12">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
                <BookOpen className="w-8 h-8 text-emerald-400 mb-3" />
                <h3 className="text-white font-semibold mb-2">Comprehensive Coverage</h3>
                <p className="text-sm text-slate-400">All 18 chapters with detailed explanations</p>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
                <Play className="w-8 h-8 text-emerald-400 mb-3" />
                <h3 className="text-white font-semibold mb-2">Video Tutorials</h3>
                <p className="text-sm text-slate-400">Professional video content for every topic</p>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
                <ClipboardCheck className="w-8 h-8 text-emerald-400 mb-3" />
                <h3 className="text-white font-semibold mb-2">Practice Exams</h3>
                <p className="text-sm text-slate-400">6 full mock exams with 100 questions each</p>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
                <Award className="w-8 h-8 text-emerald-400 mb-3" />
                <h3 className="text-white font-semibold mb-2">Expert Instructor</h3>
                <p className="text-sm text-slate-400">CFA charterholder with proven track record</p>
              </div>
            </div>

            {/* Access Status & Purchase Options */}
            <div className="mt-8">
              {isUnlocked ? (
                <div className="inline-flex items-center gap-3 bg-emerald-900/50 border border-emerald-500/30 rounded-full px-6 py-3">
                  <Unlock className="w-5 h-5 text-emerald-400" />
                  <span className="text-emerald-300">Course Unlocked</span>
                  <button
                    onClick={handleLogout}
                    className="text-slate-400 hover:text-white text-sm ml-2"
                  >
                    (Logout)
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Purchase Options */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
                    {/* Lifetime Access Option */}
                    <div className="bg-gradient-to-br from-emerald-900/40 to-slate-800/60 border border-emerald-500/30 rounded-2xl p-6 text-left">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded">BEST VALUE</span>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">Lifetime Access</h3>
                      <p className="text-3xl font-bold text-emerald-400 mb-4">CAD $99.99 <span className="text-sm font-normal text-slate-400">one-time</span></p>
                      <p className="text-sm text-slate-300 mb-4">IFC Exam Preparation - Everything You Need to Pass</p>
                      <ul className="text-sm text-slate-400 space-y-2 mb-6">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                          <span>18 chapter video lectures and podcasts</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                          <span>30 practice questions per chapter</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                          <span>6 mock exams (standard, advanced, numericals, case studies, comprehensive)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                          <span>Worked numerical solutions for every formula type</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                          <span>Flashcards, infographics, and scenario games</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                          <span>Permanent access - study at your own pace</span>
                        </li>
                      </ul>
                      <a
                        href="https://www.paypal.com/ncp/payment/TP4VL7H55HWSL"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg py-3 font-semibold text-center transition-colors"
                      >
                        Buy Lifetime Access
                      </a>
                      <p className="text-xs text-slate-500 mt-3 text-center">Access code delivered by email within 24 hours</p>
                    </div>

                    {/* Already Have Access Code Tile */}
                    <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 text-left">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded">EXISTING USER</span>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">Have Access Code?</h3>
                      <p className="text-3xl font-bold text-blue-400 mb-4">Unlock <span className="text-sm font-normal text-slate-400">instantly</span></p>
                      <p className="text-sm text-slate-300 mb-4">Already purchased or received an access code</p>
                      <ul className="text-sm text-slate-400 space-y-2 mb-6">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                          <span>Instant access to all course content</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                          <span>Enter code received via email</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                          <span>Saved for future visits</span>
                        </li>
                      </ul>
                      <button
                        onClick={() => setShowAccessModal(true)}
                        className="block w-full bg-blue-600 hover:bg-blue-500 text-white rounded-lg py-3 font-semibold text-center transition-colors"
                      >
                        <span className="flex items-center justify-center gap-2">
                          <Lock className="w-4 h-4" />
                          Enter Access Code
                        </span>
                      </button>
                      <p className="text-xs text-slate-500 mt-3 text-center">Unlock your course immediately</p>
                    </div>
                  </div>

                  {/* Contact */}
                  <p className="text-sm text-slate-500 text-center mt-6">
                    Questions? Contact <a href="mailto:edufinwizards@gmail.com" className="text-emerald-400 hover:text-emerald-300">edufinwizards@gmail.com</a>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Course Content */}
        <div className="max-w-6xl mx-auto px-6 pb-20">
          {/* Exam Information */}
          <div className="mb-16">
            <button
              onClick={() => setShowSyllabus(!showSyllabus)}
              className="w-full flex items-center justify-between bg-slate-800/50 border border-slate-700 rounded-xl px-6 py-4 text-white hover:bg-slate-800 transition-colors"
            >
              <span className="font-semibold">IFC Exam Information and Syllabus</span>
              {showSyllabus ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            
            {showSyllabus && (
              <div className="mt-4 grid md:grid-cols-2 gap-6">
                {/* Exam Structure */}
                <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Exam Structure</h3>
                  <div className="space-y-3">
                    {IFC_EXAM_INFO.structure.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-slate-400">{item.label}</span>
                        <span className="text-white font-medium">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Exam Weighting */}
                <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Exam Weighting</h3>
                  <div className="space-y-3">
                    {IFC_EXAM_INFO.weighting.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-slate-400">{item.topic}</span>
                        <span className="text-emerald-400 font-medium">{item.weight}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Chapter Tiles */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-6">Course Chapters</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {IFC_CHAPTERS.map((chapter) => (
                <div
                  key={chapter.id}
                  onClick={() => isUnlocked && setActiveChapter(chapter.id)}
                  className={`relative bg-slate-800/50 border rounded-xl p-5 transition-all ${
                    isUnlocked 
                      ? 'border-slate-700 hover:border-emerald-500/50 hover:bg-slate-800 cursor-pointer' 
                      : 'border-slate-700/50 opacity-75'
                  }`}
                >
                  {!isUnlocked && (
                    <div className="absolute top-4 right-4">
                      <Lock className="w-4 h-4 text-slate-500" />
                    </div>
                  )}
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold ${
                      isUnlocked ? 'bg-emerald-900/50 text-emerald-400' : 'bg-slate-700 text-slate-400'
                    }`}>
                      {chapter.id}
                    </div>
                    <div className="flex-1">
                      <h3 className={`font-semibold mb-1 ${isUnlocked ? 'text-white' : 'text-slate-400'}`}>
                        {chapter.title}
                      </h3>
                      <p className="text-sm text-slate-500 line-clamp-2">
                        {chapter.description}
                      </p>
                    </div>
                  </div>
                  {isUnlocked && (
                    <div className="mt-4 flex items-center gap-3 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <Play className="w-3 h-3" /> Video
                      </span>
                      {chapter.podcast && (
                        <span className="flex items-center gap-1">
                          <FileText className="w-3 h-3" /> Podcast
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Numerical Practice */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-6">Numerical Practice</h2>
            <div
              onClick={() => isUnlocked && setActiveChapter('numerical')}
              className={`relative bg-gradient-to-r from-blue-900/30 to-slate-800/50 border rounded-xl p-6 transition-all ${
                isUnlocked 
                  ? 'border-blue-500/30 hover:border-blue-400/50 cursor-pointer' 
                  : 'border-slate-700/50 opacity-75'
              }`}
            >
              {!isUnlocked && (
                <div className="absolute top-4 right-4">
                  <Lock className="w-4 h-4 text-slate-500" />
                </div>
              )}
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  isUnlocked ? 'bg-blue-900/50 text-blue-400' : 'bg-slate-700 text-slate-400'
                }`}>
                  <Calculator className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className={`text-lg font-semibold mb-2 ${isUnlocked ? 'text-white' : 'text-slate-400'}`}>
                    {NUMERICAL_PRACTICE.title}
                  </h3>
                  <p className="text-sm text-slate-400 mb-3">
                    {NUMERICAL_PRACTICE.description}
                  </p>
                  {isUnlocked && (
                    <div className="flex flex-wrap gap-2">
                      {NUMERICAL_PRACTICE.videos.map((vid, idx) => (
                        <span key={idx} className="text-xs bg-blue-900/30 text-blue-300 px-2 py-1 rounded">
                          {vid.title}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Mock Exams */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-6">Mock Examinations</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {MOCK_EXAMS.map((exam) => (
                <div
                  key={exam.id}
                  onClick={() => isUnlocked && setActiveChapter(exam.key)}
                  className={`relative bg-slate-800/50 border rounded-xl p-5 transition-all ${
                    isUnlocked
                      ? 'border-slate-700 hover:border-amber-500/50 hover:bg-slate-800 cursor-pointer' 
                      : 'border-slate-700/50 opacity-75'
                  }`}
                >
                  {!isUnlocked && (
                    <div className="absolute top-4 right-4">
                      <Lock className="w-4 h-4 text-slate-500" />
                    </div>
                  )}
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isUnlocked ? 'bg-amber-900/50 text-amber-400' : 'bg-slate-700 text-slate-400'
                    }`}>
                      <ClipboardCheck className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className={`font-semibold ${isUnlocked ? 'text-white' : 'text-slate-400'}`}>
                        {exam.title}
                      </h3>
                      <p className="text-sm text-slate-500">
                        {exam.questions} questions | {exam.duration}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Access Code Modal */}
        {showAccessModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
            <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 max-w-md w-full">
              <h2 className="text-2xl font-bold text-white mb-2">Enter Access Code</h2>
              <p className="text-slate-400 mb-6">
                Enter your course access code to unlock all content.
              </p>
              <form onSubmit={handleAccessSubmit}>
                <input
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  placeholder="Enter access code"
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 mb-4"
                />
                {accessError && (
                  <p className="text-red-400 text-sm mb-4">{accessError}</p>
                )}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowAccessModal(false)}
                    className="flex-1 bg-slate-700 hover:bg-slate-600 text-white rounded-lg py-3 font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg py-3 font-medium transition-colors"
                  >
                    Unlock Course
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default IFC;
